/**
 * @typedef {'tbd' | 'in_progress' | 'granted' | 'denied' | 'error'} PermissionStatus
 */


/**
 * @typedef {Object} IAddressRequest
 * @property {string=} country
 * @property {string=} postalcode
 * @property {string=} state
 * @property {string=} county
 * @property {string=} city
 * @property {string=} street
 * @property {string=} q
 */

/**
 * @typedef {Object} IPendingAddressRequest
 * @property {string} resultKey
 * @property {IAddressRequest} addressRequest
 * @property {DeferredPromise<ILocationAddress[], {code:number, message:string}>} promise
 */

/**
 * @typedef {Object} ILocationAddressSpecific
 * @property {string=} state
 * @property {string=} region
 * @property {string=} county
 * @property {string=} stateDistrict
 * @property {string=} district
 * @property {string=} municipality
 * @property {string=} city
 * @property {string=} town
 * @property {string=} village
 * @property {string=} borough
 * @property {string=} hamlet
 * @property {string=} suburb
 * @property {string=} road
 * @property {string=} street
 */

/**
 * @typedef {Object} ILocationAddress
 * @property {number} id
 * @property {string} displayName
 * @property {maplibregl.LngLat} lngLat
 * @property {maplibregl.LngLatBounds} lngLatBounds
 * @property {string} country
 * @property {CountryCode|""} countryCode
 * @property {string} region
 * @property {string} county
 * @property {string} town
 * @property {string} street
 * @property {string} postcode
 * @property {number|null} placeID
 * @property {number|null} zoom
 * @property {ILocationAddressSpecific} specific
 */

/**
 * @typedef {Object.<string, ILocationAddress[]>} ILocationAddresses
 */

window.OSM = window.OSM ?? {};
OSM.manager = OSM.manager ?? {};
OSM.LocationManager = class LocationManager {

    /** @type {GeolocationPosition|null} */
    lastKnownUserPosition = null;

    lastKnownUserPositionRetrievedAt = 0;
    expireRecentLocationAfter = 1000 * 60 * 10;
    /** @type {ILocationAddress|null} */
    lastKnownUserAddress = null;
    /** @type {string|null} */
    lastAddressErrorMessage = null;
    /** @type {ILocationAddresses} */
    searchResults = {};
    /** @type {IPendingAddressRequest[]} */
    pendingRequests = [];
    isRequestInProgress = false;
    /** @type {PermissionStatus} */
    permissionStatus = 'tbd';
    /** @type {OSM.DeferredPromise[]} */
    onUserLocationUpdatePromises = [];
    determiningAddress = false;

    /**
     *
     * @returns {boolean}
     */
    isUserLocationDetermined()
    {
        if(this.determiningAddress){
            return false
        }

        if(this.permissionStatus === 'granted'){
            return true;
        }
        /** @type {PermissionStatus[]} */
        const notDetermined= ['tbd', 'in_progress'];
        return !notDetermined.includes(this.permissionStatus)
    }

    /**
     *
     * @param recentLocation {boolean=}
     * @param preventPrompt {boolean}
     * @returns {Promise<void>}
     */
    async requireUserLocation(recentLocation, preventPrompt){
        recentLocation = recentLocation ?? false;
        let isRefresh = false;
        if((this.permissionStatus === 'granted') && !preventPrompt){
            if(recentLocation && (Date.now() - this.lastKnownUserPositionRetrievedAt > this.expireRecentLocationAfter)){
                isRefresh = true;
            }
        }

        const anyInProgress = (this.permissionStatus === 'in_progress' )|| this.determiningAddress

        if(!isRefresh && !anyInProgress && (this.isUserLocationDetermined() || preventPrompt)){
            return;
        }

        const promise = OSM.createDeferredPromise()
        this.onUserLocationUpdatePromises.push(promise);

        if(isRefresh || !anyInProgress){
            this.determineUserLocation().then(() => {
                for(let promise of this.onUserLocationUpdatePromises){
                    promise.resolve(null);
                }
                this.onUserLocationUpdatePromises.length = 0;
            })
        }

        return promise
    }

    async determineUserLocation()
    {

        this.lastAddressErrorMessage = null;
        this.determiningAddress = true;
        return new Promise(
            /**
             *
             * @param resolve {() => void}
             * @returns {Promise<void>}
             */
            async (resolve) =>
            {
                const position = await this.getCurrentPosition();
                if (!position)
                {
                    this.determiningAddress = false;
                    resolve();
                    return;
                }
                this.lastKnownUserPosition = position;
                this.lastKnownUserPositionRetrievedAt = Date.now();
                this.lastKnownUserAddress = null;
                return this.reverseRequestAddress(position.coords.latitude, position.coords.longitude).then(
                    /**
                     *
                     * @param address {ILocationAddress}
                     */
                    (address) => {
                        this.determiningAddress = false;
                        this.lastKnownUserAddress = address;
                        resolve();
                    }).catch(
                    /**
                     *
                     * @param error {{code:number, message:string}}
                     */
                    (error) => {
                        this.determiningAddress = false;
                        this.lastAddressErrorMessage = error.message;
                        resolve();
                    });
            })
    }

    /**
     *
     * @returns {Promise<GeolocationPosition|null>}
     */
    async getCurrentPosition()
    {
        if (!navigator.geolocation) {
            this.permissionStatus = 'error'
            return null;
        }
        this.permissionStatus = 'in_progress';
        return new Promise(
            /**
             *
             * @param resolve {(value:GeolocationPosition) => void}
             * @param reject {(value?:void) => void}
             */
            async (resolve, reject) => {

                navigator.geolocation.getCurrentPosition(
                    /**
                     *
                     * @param geoLocationPosition {GeolocationPosition}
                     */
                    (geoLocationPosition) => {
                        this.permissionStatus = 'granted';
                        resolve(geoLocationPosition)
                    },
                    /**
                     *
                     * @param error {GeolocationPositionError}
                     */
                    (error) => {
                        if(error.code === 1){
                            this.permissionStatus = 'denied'
                        } else {
                            this.permissionStatus = 'error'
                        }
                        reject();
                    }, {
                        enableHighAccuracy: true
                    });
            })
    }

    /**
     *
     * @returns {null|string}
     */
    getPermissionErrorMessage()
    {
        if(this.isGeolocationDeniedByUser())
        {
            return "Please enable geolocation in your settings."
        }
        if(this.isGeolocationUnavailable())
        {
            return "Geolocation is not currently available. Please check your settings and try again."
        }
        return null;
    }

    /**
     *
     * @returns {boolean}
     */
    isGeolocationUnavailable()
    {
        return this.permissionStatus === 'error' || this.permissionStatus === 'denied';
    }

    isGeolocationDeniedByUser()
    {
        return this.permissionStatus === 'denied';
    }

    /**
     *
     * @returns {maplibregl.LngLat|null}
     */
    getLastKnowLngLat()
    {
        if(!this.lastKnownUserPosition){
            return null;
        }
        return new maplibregl.LngLat(this.lastKnownUserPosition.coords.longitude, this.lastKnownUserPosition.coords.latitude);
    }

    /**
     *
     * @param townOrPostcode {string}
     * @param addressRequest {IAddressRequest=}
     * @param countryCodes {string[]=}
     * @returns {Promise<ILocationAddress[]>}
     */
    async getAddressWithTownOrPostcode(townOrPostcode, addressRequest, countryCodes)
    {
        if(!townOrPostcode){
            return [];
        }
        addressRequest = addressRequest ? {...addressRequest} : {};
        countryCodes = countryCodes && countryCodes.length ? countryCodes : (addressRequest.country ? [addressRequest.country] : ["gb", "ie"]);
        delete addressRequest.city;
        delete addressRequest.postalcode;
        delete addressRequest.street;
        delete addressRequest.q;

        addressRequest.city = townOrPostcode;

        for(let countryCode of countryCodes){
            addressRequest.country = countryCode;
            const address = await this.getAddress(addressRequest);
            if(address.length){
                return address;
            }
        }
        delete addressRequest.city;
        addressRequest.postalcode = townOrPostcode;
        for(let countryCode of countryCodes){
            addressRequest.country = countryCode;
            const address = await this.getAddress(addressRequest);
            if(address.length){
                return address;
            }
        }
        delete addressRequest.postalcode;
        const parts = townOrPostcode.split(" ");
        const q = (parts.length > 1 ? parts[0] : "").toUpperCase();
        if(q.startsWith("BT") && (q.length === 3 || q.length === 4)){
            addressRequest.q = q;
            const address = await this.getAddress(addressRequest);
            if(address.length){
                return address;
            }
        }
        return [];
    }


    /**
     *
     * @param addressRequest {IAddressRequest}
     * @returns {Promise<ILocationAddress[]>}
     */
    async getAddress(addressRequest)
    {
        const resultKey = JSON.stringify(OSM.keySort(JSON.parse(JSON.stringify(addressRequest))));
        const promise = OSM.createDeferredPromise()

        /** @type {IPendingAddressRequest} */
        const pendingRequest= {
            resultKey: resultKey,
            addressRequest: addressRequest,
            promise: promise
        }

        this.pendingRequests.push(pendingRequest);
        this.handleNextAddress();
        return promise;
    }

    /**
     *
     * @param countryCode {CountryCode}
     * @returns {Promise<ILocationAddress[]|null>}
     */
    async getCountryLocation(countryCode)
    {
        /** @type {IAddressRequest} */
        const request = {
            country: countryCode
        }
        return this.getAddress(request).then(
            /**
             *
             * @param itemAddresses {ILocationAddress[]}
             * @returns {ILocationAddress}
             */
            (itemAddresses) => {
                return itemAddresses[0] ?? null;
            }).catch((() => {
            return null
        }));
    }

    handleNextAddress()
    {
        /** @type {IPendingAddressRequest|null} */
        let nextRequest = null;
        for(let i = 0; i < this.pendingRequests.length; i++)
        {
            const pendingRequest = this.pendingRequests[i];
            /** @type {ILocationAddress[]|null} */
            const searchResult= this.searchResults[pendingRequest.resultKey] ?? null;
            if(searchResult !== null)
            {
                pendingRequest.promise.resolve(searchResult);
                this.pendingRequests.splice(i, 1);
                i --;
                continue;
            }
            if(!nextRequest){
                nextRequest = pendingRequest;
            }
        }
        if(!nextRequest || this.isRequestInProgress){
            return;
        }
        if(nextRequest.addressRequest.country && !nextRequest.addressRequest.city){

            const countryCode = nextRequest.addressRequest.country;
            const lngLat = this.getCountryConfigLngLat(countryCode)
            const lngLatBounds = this.getCountryConfigLngLatBounds(countryCode)
            if(lngLat && lngLatBounds)
            {
                this.searchResults[nextRequest.resultKey] = [{
                    id: 0,
                    displayName: countryCode,
                    lngLat: lngLat,
                    lngLatBounds: lngLatBounds,
                    country: countryCode,
                    countryCode:countryCode,
                    region: "",
                    county: "",
                    town: "",
                    street: "",
                    postcode: "",
                    placeID: null,
                    zoom: null,
                    specific: {

                    }
                }];
                this.isRequestInProgress = false;
                this.handleNextAddress();
                return;
            }
        }
        this.isRequestInProgress = true;
        this.requestAddresses(nextRequest.addressRequest).then(
            /**
             *
             * @param searchResult {ILocationAddress[]}
             */
            (searchResult) => {
                this.searchResults[nextRequest.resultKey] = searchResult;
                this.isRequestInProgress = false;
                this.handleNextAddress();
            }).catch(
            /**
             *
             * @param error {{code:number, message:string}}
             */
            (error) => {
                nextRequest.promise.reject(error);

                for(let i = 0; i < this.pendingRequests.length; i++)
                {
                    if(this.pendingRequests[i] === nextRequest){
                        this.pendingRequests.splice(i, 1);
                        break;
                    }
                }
                this.isRequestInProgress = false;
                this.handleNextAddress();
            })
    }

    /**
     * @param addressRequest {IAddressRequest}
     * @returns {Promise<ILocationAddress[]>}
     */
    requestAddresses(addressRequest)
    {
        let url = "https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1";
        if(addressRequest.q){
            url += "&q=" + encodeURIComponent(addressRequest.q);
        } else {
            for(let key in addressRequest){
                const value = addressRequest[key] ?? "";
                if(value !== ""){
                    url += "&"+key+"=" + encodeURIComponent(value);
                }
            }
        }
        return new Promise(
            /**
             *
             * @param resolve {(value:ILocationAddress[]) => void}
             * @param reject {(value:{code:number, message:string}) => void}
             */
            async (resolve, reject) => {
                fetch(url, {
                    method: 'GET',
                    headers: {
                        'Connection': 'keep-alive',
                        'Content-Type': 'application/json; charset=UTF-8'
                    }
                })
                    .then(
                        /**
                         *
                         * @param response {Response}
                         */
                        (response) => {
                            return response.json();
                        })
                    .then(
                        (response) => {
                            const values = Array.isArray(response) ? response : [];
                            /** @type {ILocationAddress[]} */
                            const addresses = [];
                            for(let i = 0 ; i < values.length ; i++){
                                const fromData = typeof values[i] === "object" ? values[i] : {};
                                const address = this.parseAddressResponse(fromData);
                                if(address){
                                    addresses.push(address)
                                }
                            }
                            resolve(addresses);
                        })
                    .catch(
                        /**
                         *
                         * @param error {*}
                         */
                        (error) => {
                            reject({code: 404, message: "Failed to load response data"});
                        })
            });
    }

    /**
     *
     * @param latitude {number}
     * @param longitude {number}
     * @returns {Promise<ILocationAddress>}
     */
    async reverseRequestAddress(latitude, longitude){

        return new Promise(
            /**
             *
             * @param resolve {(value:ILocationAddress) => void}
             * @param reject {(value:{code:number, message:string}) => void}
             */
            async (resolve, reject) => {
                fetch("https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&lat="+latitude+"&lon="+longitude, {
                    method: 'GET',
                    headers: {
                        'Connection': 'keep-alive',
                        'Content-Type': 'application/json; charset=UTF-8'
                    }
                })
                    .then(
                        /**
                         *
                         * @param response {Response}
                         */
                        (response) => {
                            return response.json();
                        })
                    .then(
                        (response) => {
                            const address = this.parseAddressResponse(response);
                            if(!address){
                                return reject({code: 404, message: "Address not found"});
                            }
                            resolve(address);
                        })
                    .catch(
                        /**
                         *
                         * @param error {*}
                         */
                        (error) => {
                            return reject({code: 404, message: "Failed to load response data"});
                        })
            });
    }
    /**
     *
     * @param fromData {Object.<string, *>}
     * @returns {null}
     */
    parseAddressResponse(fromData)
    {
        fromData = typeof fromData === 'object' ? fromData : {};
        /** @type {string} */
        const displayName = fromData.display_name ?? "";
        const latitude = OSM.isNumeric(fromData.lat) ? parseFloat(fromData.lat) : 0;
        const longitude = OSM.isNumeric(fromData.lon) ? parseFloat(fromData.lon) : 0;
        /** @type {Object.<string, *>}} */
        const fromAddress = typeof fromData.address === 'object' ? fromData.address : {};

        const boundingBox = Array.isArray(fromData.boundingbox ?? null) ? fromData.boundingbox : [];
        /** @type {maplibregl.LngLatBounds|null} */
        let lngLatBounds = null;
        if(boundingBox.length >= 4){
            const swLatitude = OSM.isNumeric(boundingBox[0]) ? parseFloat(boundingBox[0]) : 0;
            const swLongitude = OSM.isNumeric(boundingBox[2]) ? parseFloat(boundingBox[2]) : 0;
            const neLatitude = OSM.isNumeric(boundingBox[1]) ? parseFloat(boundingBox[1]) : 0;
            const neLongitude = OSM.isNumeric(boundingBox[3]) ? parseFloat(boundingBox[3]) : 0;
            if(swLatitude || swLongitude || neLatitude || neLongitude){
                lngLatBounds = new maplibregl.LngLatBounds(new maplibregl.LngLat(swLongitude, swLatitude), new maplibregl.LngLat(neLongitude, neLatitude));
            }
        }
        if(!displayName || !(latitude || longitude) || !Object.keys(fromAddress).length || !lngLatBounds){
            return null;
        }


        /** @type {ILocationAddress} */
        const address = {
            id: parseInt(fromData.place_id ?? 0),
            displayName: displayName,
            lngLat: new maplibregl.LngLat(longitude, latitude),
            lngLatBounds: lngLatBounds,
            country: fromAddress.country ?? (fromAddress.country_code  ?? ""),
            countryCode: (fromAddress.country_code ?? "").toLowerCase(),
            region: fromAddress.state ?? (fromAddress.region ?? ""),
            county: fromAddress.county ?? (fromAddress.state_district ?? (fromAddress.district ?? (fromAddress.municipality ?? ""))),
            town: fromAddress.city ?? (fromAddress.town ?? (fromAddress.village ?? (fromAddress.borough ?? (fromAddress.hamlet ?? (fromAddress.suburb ?? ""))))),
            street: fromAddress.road ?? (fromAddress.street ?? ""),
            postcode: fromAddress.postcode ?? "",
            placeID: fromAddress.place_id ? parseInt(fromAddress.place_id) : null,
            zoom: null,
            specific: {}
        }
        if(fromAddress.state){
            address.specific.state = fromAddress.state;
        }
        if(fromAddress.region){
            address.specific.region = fromAddress.region;
        }
        if(fromAddress.county){
            address.specific.county = fromAddress.county;
        }
        if(fromAddress.state_district){
            address.specific.stateDistrict = fromAddress.state_district;
        }
        if(fromAddress.district){
            address.specific.district = fromAddress.district;
        }
        if(fromAddress.municipality){
            address.specific.municipality = fromAddress.municipality;
        }
        if(fromAddress.city){
            address.specific.city = fromAddress.city;
        }
        if(fromAddress.town){
            address.specific.town = fromAddress.town;
        }
        if(fromAddress.village){
            address.specific.village = fromAddress.village;
        }
        if(fromAddress.borough){
            address.specific.borough = fromAddress.borough;
        }
        if(fromAddress.hamlet){
            address.specific.hamlet = fromAddress.hamlet;
        }
        if(fromAddress.suburb){
            address.specific.suburb = fromAddress.suburb;
        }
        return address;
    }

    /**
     *
     * @param countryCode {CountryCode}
     * @returns {maplibregl.LngLat|null}
     */
    getCountryConfigLngLat(countryCode)
    {
        const countryLocationConfig = OSM.CountryLocations[countryCode] ?? null;
        if(!countryLocationConfig){
            return null;
        }
        if(
            ((countryLocationConfig.lat ?? null) === null) ||
            ((countryLocationConfig.lng ?? null) === null)
        )
        {
            return null;
        }
        return new maplibregl.LngLat(countryLocationConfig.lng, countryLocationConfig.lat)
    }

    /**
     *
     * @param countryCode {CountryCode}
     * @returns {maplibregl.LngLatBounds|null}
     */
    getCountryConfigLngLatBounds(countryCode)
    {
        const countryLocationConfig = OSM.CountryLocations[countryCode] ?? null;
        if(!countryLocationConfig){
            return null;
        }
        return this.determineBounds(countryLocationConfig.lat, countryLocationConfig.lng, countryLocationConfig.width, countryLocationConfig.height)
    }

    /**
     *
     * @param lngLat1 {maplibregl.LngLat}
     * @param lngLat2 {maplibregl.LngLat}
     * @returns {number}
     */
    getDistanceInMetresBetween(lngLat1, lngLat2)
    {
        const lat1 = OSM.degreesToRadians(lngLat1.lat);
        const lng1 = OSM.degreesToRadians(lngLat1.lng);
        const lat2 = OSM.degreesToRadians(lngLat2.lat);
        const lng2 = OSM.degreesToRadians(lngLat2.lng);

        return Math.round((6371 * 1000 * Math.acos( Math.cos( lat2 ) * Math.cos( lat1 ) * Math.cos( lng1 - lng2 ) + Math.sin( lat2 ) * Math.sin( lat1 ) )));
    }

    /**
     *
     * @param bounds {maplibregl.LngLatBounds}
     * @returns {number}
     */
    getBoundsWidth(bounds)
    {
        const east = bounds.getEast();
        const west = bounds.getWest();
        return east - west;
    }

    /**
     *
     * @param bounds {maplibregl.LngLatBounds}
     * @returns {number}
     */
    getBoundsHeight(bounds)
    {
        const north = bounds.getNorth();
        const south = bounds.getSouth();
        return north - south;
    }

    /**
     *
     * @param centreLat {number}
     * @param centreLng {number}
     * @param width {number}
     * @param height {number}
     * @returns {maplibregl.LngLatBounds}
     */
    determineBounds(centreLat, centreLng, width, height)
    {
        const north = centreLat + height / 2;
        const south = centreLat - height / 2;
        const west = centreLng - width / 2;
        const east = centreLng + width / 2;

        return new maplibregl.LngLatBounds(new maplibregl.LngLat(west, south), new maplibregl.LngLat(east, north));
    }

    /**
     *
     * @param distanceInMetres {number}
     * @returns {string}
     */
    getReadableDistance(distanceInMetres)
    {
        let metric;
        let value;
        if(distanceInMetres < 1000)
        {
            metric = "m";
            value = distanceInMetres;
        } else {
            metric = "km";
            if(distanceInMetres < 10000)
            {
                value = Math.round(distanceInMetres / 100) / 10;
            } else {
                value = Math.round(distanceInMetres / 1000);
            }
        }
        return OSM.formatNumber(value)+metric;
    }
}

/**
 *
 * @returns {LocationManager}
 */
OSM.manager.getLocationManager = () => {
    if(OSM.manager.location){
        return OSM.manager.location
    }
    OSM.manager.location = new OSM.LocationManager();
    return OSM.manager.location
}